/*
 * Trademarks — public notice page listing every mark owned by Selah & Stone
 * Legacy Holdings LLC.
 *
 * Purpose:
 *   1. Legal notice. A dedicated /trademarks page is standard for tech
 *      companies (Microsoft, Adobe, Google all have one) and provides a
 *      permanent, timestamped notice of the marks in commerce. This helps
 *      defeat any future claim of abandonment or genericization.
 *   2. USPTO examiner reference. Examining attorneys sometimes visit
 *      trademark-notice pages when reviewing an application; a clean, formal
 *      page reinforces that the mark is used consistently.
 *   3. Press / partner reference. Bloggers and integrators can quickly find
 *      the correct spelling, symbol, and attribution.
 *
 * Route: /trademarks (public, no auth required).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, ShieldCheck, Download, FileText, Archive } from 'lucide-react';
import { Card } from '../components/ui/card';
import Disclaimer from '../components/Disclaimer';
import { isIOS } from '../utils/platform';

// Inside the iOS App Store build, references to competing storefronts
// (Google Play, Microsoft Store) — even when describing a DIFFERENT
// Selah product like Selah Slate — can be interpreted as anti-steering
// language under Apple App Review Guideline 3.1.1. This helper strips
// or normalises those references at render time.
const sanitizeSinceForIOS = (text) => {
  if (!text) return text;
  if (!isIOS()) return text;
  return text
    .replace(/Live on Apple App Store, Google Play, and Microsoft Store/gi, 'Live in multiple app stores')
    .replace(/Google Play/gi, 'multiple stores')
    .replace(/Microsoft Store/gi, 'multiple stores');
};

const PAL = {
  bg: '#121212',
  card: '#1C1E21',
  cardSoft: '#17191B',
  hairline: 'rgba(253, 251, 247, 0.08)',
  alabaster: '#FDFBF7',
  fgSubtle: 'rgba(253, 251, 247, 0.72)',
  fgMuted: 'rgba(253, 251, 247, 0.52)',
  fgFaint: 'rgba(253, 251, 247, 0.35)',
  rose: '#B76E79',
  roseBlush: '#E0BFB8',
  sage: '#7A946C',
};

const MARKS = [
  {
    id: 'downloads',
    mark: 'Selah Downloads',
    tm: '\u2122',
    tagline: 'Encrypted spiritual journal software with AI-assisted insights.',
    classes: 'Class 009 · Class 042 · Class 045',
    status: 'in-use',
    since: 'April 2026',
    url: 'https://selahdownloads.com',
    notes:
      'Includes the sub-feature Selah Rest\u2122 (a 60-second guided reflection module).',
  },
  {
    id: 'rest',
    mark: 'Selah Rest',
    tm: '\u2122',
    tagline: 'A 60-second guided reflection feature within Selah Downloads.',
    classes: 'Filed alongside Selah Downloads (feature-mark)',
    status: 'in-use',
    since: 'July 2026',
    url: 'https://selahdownloads.com',
    notes:
      'Selah Rest is a proprietary feature and named module of the Selah Downloads software.',
  },
  {
    id: 'slate',
    mark: 'Selah Slate',
    tm: '\u2122',
    tagline: 'Free, faith-based FCRA credit-dispute companion software.',
    classes: 'Class 009 · Class 042',
    status: 'in-use',
    since: 'Live on Apple App Store, Google Play, and Microsoft Store',
    url: 'https://selahslate.com',
    notes:
      'Live and freely available at selahslate.com and on major app-store platforms.',
  },
  {
    id: 'research',
    mark: 'Selah and Stone Research',
    tm: '\u2122',
    tagline:
      'Boutique due-diligence and analytical research services under "The Selah Method".',
    classes: 'Class 035 · Class 042',
    status: 'in-use',
    since: 'Live at selahandstoneresearch.com',
    url: 'https://selahandstoneresearch.com',
    notes:
      'Verified public-record research and structured reporting; operates as a Consumer Reporting Agency under the FCRA for permissible-purpose engagements.',
  },
  {
    id: 'home',
    mark: 'Selah & Salt Home',
    tm: '\u2122',
    tagline: 'Scripture-anchored homemaking software and hospitality tools.',
    classes: 'Class 009 · Class 042 (planned)',
    status: 'itu',
    since: 'Intent-to-Use \u2014 launch forthcoming',
    url: null,
    notes:
      'Trademark application filed under \u00a71(b) Intent to Use. Product is under development.',
  },
];

function StatusPill({ status }) {
  const config =
    status === 'itu'
      ? { label: 'Intent to Use', color: PAL.roseBlush }
      : { label: 'In active use', color: PAL.sage };
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] uppercase tracking-[0.22em] font-semibold"
      style={{
        color: config.color,
        background: `${config.color}18`,
        border: `1px solid ${config.color}44`,
      }}
      data-testid={`trademark-status-${status}`}
    >
      <span
        aria-hidden
        className="inline-block w-1.5 h-1.5 rounded-full"
        style={{ background: config.color, opacity: 0.9 }}
      />
      {config.label}
    </span>
  );
}

function MarkCard({ item }) {
  return (
    <article
      className="rounded-2xl overflow-hidden"
      style={{
        background: `linear-gradient(180deg, ${PAL.card} 0%, ${PAL.cardSoft} 100%)`,
        border: `1px solid ${PAL.hairline}`,
        boxShadow: '0 10px 30px -18px rgba(0,0,0,0.7)',
      }}
      data-testid={`trademark-card-${item.id}`}
    >
      <div
        aria-hidden
        className="h-[3px] w-full"
        style={{
          background: `linear-gradient(90deg, transparent 0%, ${PAL.rose} 30%, ${PAL.roseBlush} 70%, transparent 100%)`,
          opacity: 0.85,
        }}
      />
      <div className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
          <div>
            <h2
              className="text-[24px] leading-tight font-semibold"
              style={{
                color: PAL.alabaster,
                fontFamily: '"Playfair Display", serif',
                letterSpacing: '-0.005em',
              }}
            >
              {item.mark}
              <sup className="text-[13px] ml-0.5" style={{ color: PAL.roseBlush }}>
                {item.tm}
              </sup>
            </h2>
            <p
              className="text-[13px] italic mt-1"
              style={{ color: PAL.roseBlush, fontFamily: '"Crimson Text", serif' }}
            >
              {item.tagline}
            </p>
          </div>
          <StatusPill status={item.status} />
        </div>

        <dl className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-3 mt-5 text-[12.5px]">
          <div>
            <dt
              className="uppercase tracking-[0.18em] text-[10.5px] mb-1"
              style={{ color: PAL.fgMuted }}
            >
              Class(es)
            </dt>
            <dd style={{ color: PAL.fgSubtle }}>{item.classes}</dd>
          </div>
          <div>
            <dt
              className="uppercase tracking-[0.18em] text-[10.5px] mb-1"
              style={{ color: PAL.fgMuted }}
            >
              In commerce since
            </dt>
            <dd style={{ color: PAL.fgSubtle }}>{sanitizeSinceForIOS(item.since)}</dd>
          </div>
          <div>
            <dt
              className="uppercase tracking-[0.18em] text-[10.5px] mb-1"
              style={{ color: PAL.fgMuted }}
            >
              Live at
            </dt>
            <dd>
              {item.url ? (
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 underline underline-offset-2"
                  style={{ color: PAL.roseBlush }}
                >
                  {item.url.replace(/^https?:\/\//, '')}
                  <ExternalLink size={11} />
                </a>
              ) : (
                <span style={{ color: PAL.fgMuted }}>—</span>
              )}
            </dd>
          </div>
        </dl>

        {item.notes && (
          <p
            className="text-[12.5px] leading-relaxed mt-5 pt-4"
            style={{
              borderTop: `1px solid ${PAL.hairline}`,
              color: PAL.fgSubtle,
            }}
          >
            {item.notes}
          </p>
        )}
      </div>
    </article>
  );
}

export default function Trademarks() {
  return (
    <div
      className="force-night min-h-screen flex flex-col"
      style={{ background: PAL.bg, color: PAL.alabaster }}
      data-testid="trademarks-page"
    >
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-32 -right-24 w-[420px] h-[420px] rounded-full"
          style={{
            background:
              'radial-gradient(circle, rgba(183,110,121,0.16) 0%, rgba(0,0,0,0) 70%)',
            filter: 'blur(20px)',
          }}
        />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-8">
          <Link
            to="/"
            data-testid="trademarks-back-link"
            className="inline-flex items-center gap-1.5 text-[12px] mb-8"
            style={{ color: PAL.fgMuted }}
          >
            <ArrowLeft size={13} />
            Back to Selah Downloads
          </Link>
          <div className="flex items-start gap-3 mb-4">
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
              style={{
                background: 'rgba(183, 110, 121, 0.10)',
                border: `1px solid ${PAL.hairline}`,
              }}
            >
              <ShieldCheck size={19} style={{ color: PAL.rose }} />
            </div>
            <p
              className="text-[10.5px] uppercase tracking-[0.32em] pt-3"
              style={{ color: PAL.roseBlush }}
            >
              Trademarks &amp; Attribution
            </p>
          </div>
          <h1
            className="text-[36px] sm:text-[46px] leading-[1.1] font-semibold mb-4"
            style={{
              color: PAL.alabaster,
              fontFamily: '"Playfair Display", serif',
              letterSpacing: '-0.015em',
            }}
          >
            The marks and names of Selah &amp; Stone Legacy Holdings.
          </h1>
          <p
            className="text-[15px] sm:text-[16px] leading-relaxed max-w-2xl"
            style={{ color: PAL.fgSubtle, fontFamily: '"Crimson Text", serif' }}
          >
            The trademarks, service marks, and trade names listed below
            identify products and services offered by <b>Selah &amp; Stone
            Legacy Holdings LLC</b>. Use of any mark without written permission
            is prohibited. Federal and common-law rights are reserved.
          </p>
        </div>
      </section>

      {/* Mark grid */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-14">
        <div
          className="grid grid-cols-1 gap-5"
          data-testid="trademarks-grid"
        >
          {MARKS.map((m) => (
            <MarkCard key={m.id} item={m} />
          ))}
        </div>

        {/* Legal panel */}
        <Card
          className="mt-10 rounded-2xl p-6"
          style={{
            background: PAL.card,
            border: `1px solid ${PAL.hairline}`,
          }}
          data-testid="trademarks-legal-panel"
        >
          <h3
            className="text-[15px] font-semibold mb-3"
            style={{ color: PAL.alabaster }}
          >
            Usage &amp; permissions
          </h3>
          <ul
            className="list-disc pl-5 text-[13px] leading-relaxed space-y-2"
            style={{ color: PAL.fgSubtle }}
          >
            <li>
              All marks above are used with the <b>&trade;</b> symbol until the
              corresponding U.S. federal registration issues, at which point the
              <b> &reg;</b> symbol will replace it.
            </li>
            <li>
              Third parties may reference any mark in accurate,
              nominative-fair-use contexts (news, reviews, integration
              disclosures) provided the mark is spelled and capitalized as shown,
              includes the <b>&trade;</b>, and includes attribution to
              <em> Selah &amp; Stone Legacy Holdings LLC</em>.
            </li>
            <li>
              No implied license or endorsement is granted by referencing the
              marks. Written permission is required for any commercial use,
              domain registration, social handle, or derivative branding that
              incorporates any of the marks.
            </li>
            <li>
              Report suspected infringement or improper use to{' '}
              <a
                href="mailto:legal@selahandstonelegacy.com"
                className="underline underline-offset-2"
                style={{ color: PAL.roseBlush }}
              >
                legal@selahandstonelegacy.com
              </a>
              .
            </li>
          </ul>
        </Card>
      </section>

      <Disclaimer />
    </div>
  );
}
